// ===================== Weapon evolutions =====================
// Max a base weapon AND own its paired passive to fuse them into an evolved
// weapon — the genre's signature build payoff. Evolved weapons are flagged
// `evolution: true` so they never appear in the normal "learn a weapon" pool.
import { defineWeapon } from '../core/registries.js';
import { CONFIG } from '../config.js';
import { TAU } from '../engine/math.js';
import { Audio } from '../engine/audio.js';

const elColor = (game) => CONFIG.colors[game.player.element] || CONFIG.colors.neutral;

// Evolution stat curves are pinned to the base weapon they replace: raw
// throughput (count × damage / cooldown) at evolution L1 must be ≥ the base
// weapon's at L8 — an evolution is a payoff, never a downgrade at pickup.
// Where the base grows projectile count with level, the evolution CONTINUES
// that count rather than restarting it. Max-level targets: ~1.6–2.2× the base
// L8 for flat-pattern evolutions, ~2.8–3.0× ceiling for the spike ones
// (railfang / eventhorizon / supernova). Modelled, not guessed — re-run the
// throughput model if you touch these.

// baseWeaponId -> { req: passiveId, into: evolvedId }
export const EVOLUTIONS = {
  bolt: { req: 'might', into: 'railfang' },
  orbit: { req: 'growth', into: 'eventhorizon' },
  nova: { req: 'velocity', into: 'supernova' },
  whip: { req: 'haste', into: 'reaper' },
  spark: { req: 'fortune', into: 'stormcall' },
  boomerang: { req: 'growth', into: 'saturn' },
  mine: { req: 'might', into: 'singularity' },
  aura: { req: 'regen', into: 'soulreaver' },
  zones: { req: 'duration', into: 'miasma' },
  strikes: { req: 'fortune', into: 'tempest' },
};

// ---- Railfang (Arc Bolt + Might) ----
defineWeapon({
  id: 'railfang', name: 'Railfang', icon: '⇻', evolution: true, maxLevel: 5,
  desc: 'EVOLVED Arc Bolt — hypersonic rails that pierce everything.',
  stats: (l) => ({ cooldown: Math.max(0.38, 0.50 - l * 0.02), damage: 37 + l * 4, count: 3 + Math.floor(l / 2), speed: 920, pierce: 999 }),
  fire(game, s) {
    const p = game.player;
    const { count, dmgMult } = game.mult(s.count);
    const spread = count > 1 ? 0.12 : 0;
    for (let i = 0; i < count; i++) {
      const a = p.aimAngle + (i - (count - 1) / 2) * spread;
      const sp = s.speed * p.projSpeedMult;
      game.spawnProjectile({ kind: 'bolt', x: p.x, y: p.y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, r: 9, drawScale: 1.8, dmg: game.baseDamage(s.damage) * dmgMult, pierce: s.pierce, life: 1.2, color: elColor(game), knockback: 110, weaponId: 'railfang' });
    }
    Audio.shoot(p.element);
  },
});

// ---- Event Horizon (Orbit Shards + Growth) ----
defineWeapon({
  id: 'eventhorizon', name: 'Event Horizon', icon: '⊛', evolution: true, maxLevel: 5,
  desc: 'EVOLVED Orbit Shards — a wide, fast ring of crushing orbs.',
  stats: (l) => ({ cooldown: 1.9, damage: 21 + l * 5, count: 5 + l, orbitRadius: 120 + l * 9, orbitSpeed: 4.4 + l * 0.15 }),
  fire(game, s) {
    const p = game.player;
    const { count, dmgMult } = game.mult(s.count);
    for (let i = 0; i < count; i++) {
      const ang = (i / count) * TAU;
      game.spawnProjectile({ kind: 'orbit', x: p.x, y: p.y, r: 17, dmg: game.baseDamage(s.damage) * dmgMult, pierce: -1, rehit: 0.3, life: s.cooldown + 0.08, color: elColor(game), knockback: 70, orbitAngle: ang, orbitRadius: s.orbitRadius, orbitSpeed: s.orbitSpeed, weaponId: 'eventhorizon' });
    }
  },
});

// ---- Supernova (Nova Burst + Velocity) ----
defineWeapon({
  id: 'supernova', name: 'Supernova', icon: '☀', evolution: true, maxLevel: 5,
  desc: 'EVOLVED Nova Burst — a full ring plus a devastating core blast.',
  stats: (l) => ({ cooldown: Math.max(1.2, 2.0 - l * 0.08), damage: 25 + l * 4.5, count: 14 + l * 2, speed: 440, area: 170 + l * 12 }),
  fire(game, s) {
    const p = game.player;
    const { count, dmgMult } = game.mult(s.count);
    const off = game.rng.next() * TAU;
    for (let i = 0; i < count; i++) {
      const a = off + (i / count) * TAU;
      const sp = s.speed * p.projSpeedMult;
      game.spawnProjectile({ kind: 'bolt', x: p.x, y: p.y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, r: 7, dmg: game.baseDamage(s.damage) * dmgMult, pierce: 1, life: 1.2, color: elColor(game), knockback: 60, weaponId: 'supernova' });
    }
    game.spawnProjectile({ kind: 'aoe', x: p.x, y: p.y, dmg: game.baseDamage(s.damage) * 1.5 * dmgMult, maxR: s.area * p.areaMult, color: elColor(game), life: 0.34, pierce: -1, rehit: 0, weaponId: 'supernova' });
    game.camera.shake(6, 0.18);
    Audio.shoot(p.element);
  },
});

// ---- Reaper's Embrace (Spectral Whip + Haste) ----
defineWeapon({
  id: 'reaper', name: "Reaper's Embrace", icon: '☠', evolution: true, maxLevel: 5,
  desc: 'EVOLVED Spectral Whip — a full 360° scythe that reaps all around you.',
  stats: (l) => ({ cooldown: Math.max(0.42, 0.65 - l * 0.03), damage: 55 + l * 3, reach: 150 + l * 12, count: 1 }),
  fire(game, s) {
    const p = game.player;
    // MULTIPLICATION manifests as visible echo scythes: each extra count is a
    // real full-circle melee sweep, offset around the ring so the blades fan
    // out. Overflow past the projectile cap still folds into dmgMult, so the
    // evolution honors the same investment the base whip did.
    const { count, dmgMult } = game.mult(s.count);
    for (let i = 0; i < count; i++) {
      game.spawnProjectile({ kind: 'melee', x: p.x, y: p.y, r: s.reach, reach: s.reach, arcHalf: Math.PI, baseAngle: (i * TAU) / count, sweep: TAU, life: 0.28, dmg: game.baseDamage(s.damage) * dmgMult, pierce: -1, rehit: 0.28, color: elColor(game), knockback: 150, weaponId: 'reaper' });
    }
    Audio.shoot(p.element);
  },
});

// ---- Stormcall (Homing Spark + Fortune) ----
defineWeapon({
  id: 'stormcall', name: 'Stormcall', icon: '⟿', evolution: true, maxLevel: 5,
  desc: 'EVOLVED Homing Spark — seeking bolts that chain lightning on every hit.',
  stats: (l) => ({ cooldown: 0.8, damage: 33 + l * 3, count: 5 + Math.floor(l / 2), speed: 300 + l * 10, turn: 6.5, chain: 3 + Math.floor(l / 2) }),
  fire(game, s) {
    const p = game.player;
    const { count, dmgMult } = game.mult(s.count);
    for (let i = 0; i < count; i++) {
      const a = game.rng.angle();
      const sp = s.speed * p.projSpeedMult;
      game.spawnProjectile({ kind: 'homing', x: p.x, y: p.y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, r: 8, dmg: game.baseDamage(s.damage) * dmgMult, pierce: 0, life: 2.6, speed: sp, turn: s.turn, chain: s.chain, color: CONFIG.colors.lightning, knockback: 40, weaponId: 'stormcall' });
    }
    Audio.shoot('lightning');
  },
});

// ---- Ring of Saturn (Razor Ring + Growth) ----
defineWeapon({
  id: 'saturn', name: 'Ring of Saturn', icon: '🪐', evolution: true, maxLevel: 5,
  desc: 'EVOLVED Razor Ring — a colossal blade that scythes the whole screen and returns.',
  stats: (l) => ({ cooldown: Math.max(1.1, 1.5 - l * 0.06), damage: 50 + l * 3, count: 3 + Math.floor(l / 3), speed: 300 }),
  fire(game, s) {
    const p = game.player;
    const { count, dmgMult } = game.mult(s.count);
    const spread = count > 1 ? TAU / count : 0;
    for (let i = 0; i < count; i++) {
      const a = p.aimAngle + i * spread;
      const sp = s.speed * p.projSpeedMult;
      game.spawnProjectile({ kind: 'boomerang', x: p.x, y: p.y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, r: 22, drawScale: 1.9, dmg: game.baseDamage(s.damage) * dmgMult, pierce: -1, rehit: 0.32, life: 2.6, speed: sp, color: elColor(game), knockback: 120, weaponId: 'saturn' });
    }
    Audio.shoot(p.element);
  },
});

// ---- Singularity (Void Mine + Might) ----
defineWeapon({
  id: 'singularity', name: 'Singularity', icon: '⚫', evolution: true, maxLevel: 5,
  desc: 'EVOLVED Void Mine — implodes, dragging foes into a catastrophic blast.',
  stats: (l) => ({ cooldown: 2.0, damage: 61 + l * 4, count: 5 + Math.floor(l / 2), area: 120 + l * 9 }),
  fire(game, s) {
    const p = game.player;
    const { count, dmgMult } = game.mult(s.count);
    for (let i = 0; i < count; i++) {
      const a = game.rng.angle();
      const d = game.rng.range(30, 110);
      game.spawnProjectile({ kind: 'mine', x: p.x + Math.cos(a) * d, y: p.y + Math.sin(a) * d, vx: 0, vy: 0, r: 13, dmg: game.baseDamage(s.damage) * dmgMult, pierce: -1, rehit: 0, life: 6, area: s.area, trigger: 60, pull: true, color: '#c98aff', weaponId: 'singularity' });
    }
  },
});

// ---- Soul Reaver (Warding Circle + Regeneration) ----
defineWeapon({
  id: 'soulreaver', name: 'Soul Reaver', icon: '⊚', evolution: true, maxLevel: 5,
  desc: 'EVOLVED Warding Circle — a vast searing halo that feeds you life.',
  stats: (l) => ({ cooldown: Math.max(0.26, 0.5 - l * 0.02), damage: 31 + l * 2.5, area: 130 + l * 15, knockback: 70 }),
  fire(game, s) {
    const p = game.player;
    const r = s.area * p.areaMult;
    // Tick weapons have no projectile count for MULTIPLICATION to multiply, so
    // mirror how mult() scales a 4-projectile fan: the count ratio and any
    // overflow damage fold into one effective damage multiplier.
    const m = game.mult(4);
    const dmg = game.baseDamage(s.damage) * (m.count / 4) * m.dmgMult;
    let hits = 0;
    for (const e of game.enemiesInRadius(p.x, p.y, r)) {
      const dx = e.x - p.x, dy = e.y - p.y;
      const d = Math.hypot(dx, dy) || 1;
      game.dealDamage(e, dmg, { knockback: s.knockback, kx: dx / d, ky: dy / d, applyElement: true, weaponId: 'soulreaver' });
      hits++;
    }
    if (hits > 0 && p.hp < p.maxHp) p.heal(Math.min(hits, 4) * 0.4);
    game.spawnProjectile({ kind: 'aura', x: p.x, y: p.y, r, baseR: s.area, dmg: 0, pierce: 0, rehit: 0, life: s.cooldown * 1.1, color: '#ff2d9b', weaponId: 'soulreaver' });
  },
});

// ---- Miasma (Hex Pools + Duration) ----
defineWeapon({
  id: 'miasma', name: 'Miasma', icon: '☷', evolution: true, maxLevel: 5,
  desc: 'EVOLVED Hex Pools — sprawling clouds of decay that smother the field.',
  stats: (l) => ({ cooldown: 1.4, damage: 24 + l * 2.5, count: 5 + Math.floor(l / 2), area: 92 + l * 9, duration: 4.5 }),
  fire(game, s) {
    const p = game.player;
    const { count, dmgMult } = game.mult(s.count);
    for (let i = 0; i < count; i++) {
      const a = game.rng.angle();
      const d = game.rng.range(30, 200);
      game.spawnProjectile({ kind: 'zone', x: p.x + Math.cos(a) * d, y: p.y + Math.sin(a) * d, r: s.area, baseR: s.area, dmg: game.baseDamage(s.damage) * dmgMult, pierce: -1, rehit: 0.35, life: s.duration * p.durationMult, color: elColor(game), weaponId: 'miasma', spin: game.rng.range(0, 6) });
    }
  },
});

// ---- Tempest (Judgement + Fortune) ----
defineWeapon({
  id: 'tempest', name: 'Tempest', icon: '⚡', evolution: true, maxLevel: 5,
  desc: 'EVOLVED Judgement — a relentless storm of bolts that stuns and shreds.',
  stats: (l) => ({ cooldown: 1.0, damage: 46 + l * 4, count: 6 + Math.floor(l / 2), area: 72 + l * 5 }),
  fire(game, s) {
    const p = game.player;
    const { count, dmgMult } = game.mult(s.count);
    const targets = game.enemiesInRadius(p.x, p.y, 780);
    for (let i = 0; i < count; i++) {
      const e = targets.length ? game.rng.pick(targets) : null;
      const x = e ? e.x : p.x + game.rng.range(-360, 360);
      const y = e ? e.y : p.y + game.rng.range(-360, 360);
      if (e && !e.boss) e.freezeT = Math.max(e.freezeT, 0.4);
      game.spawnProjectile({ kind: 'aoe', x, y, dmg: game.baseDamage(s.damage) * dmgMult, maxR: s.area * p.areaMult, color: CONFIG.colors.lightning, life: 0.3, pierce: -1, rehit: 0, applyElement: true, weaponId: 'tempest' });
      game.particles_spark(x, y, CONFIG.colors.lightning, 6);
    }
    Audio.shoot('lightning');
  },
});
